import {Page, PageRenderer} from '@/modules/report_server/domain/page'
import {PageGenerator} from '@/modules/report_server/domain/page_generator'
import {ContentProvider} from '@/modules/report_server/domain/content_provider'

import PageFacturae from '@/modules/facturae/infra/pdf_view/PageFacturae.vue'
import {nextTick} from '@/modules/report_server/infra/next_tick'
import { Invoiceable } from '../../domain/Invoiceable'
import { Configuration } from '../../domain/Configuration'
import { ProductConfiguration } from '../../domain/Product'

export class FacturaePage implements Page {
    renderer:PageRenderer|undefined
    id:string
    component:any
    visible=false;
    pageNum=0;
    showHeader:boolean=false;
    showLines:boolean=false;
    showLinesHeader:boolean=false;
    firstPrintedLine=-1
    lastPrintedLine=-1
    invoiceable: Invoiceable
    rectified_inv: Invoiceable|undefined
    configuration: Configuration
    productConfiguration: ProductConfiguration|undefined
    additionalInfoChunks: string[] = []
    pageComplete: boolean = false

    constructor(invoiceable:Invoiceable, configuration: Configuration, rectified_inv?: Invoiceable, productConfiguration?: ProductConfiguration, additionalInfoChunks: string[] = []) {
        this.id='facturae'
        this.invoiceable = invoiceable
        this.configuration = configuration
        this.rectified_inv = rectified_inv
        this.productConfiguration = productConfiguration
        this.additionalInfoChunks = additionalInfoChunks
        this.component = {...PageFacturae}
    }

    async setPageNum(pageNum: number): Promise<void> {
        return new Promise((resolve) => {
            nextTick(() => {
                this.pageNum = pageNum
                this.id = this.pageNum.toString(10)
                resolve()
            })
        })
    }

    async setVisible(visible: boolean): Promise<void> {
        return new Promise((resolve) => {
            nextTick(() => {
                this.visible = visible
                resolve()
            })
        })
    }
    setRenderer(renderer: PageRenderer): void {
        this.renderer = renderer
    }
}
// Número máximo de líneas de texto por página de additional_info
const ADDITIONAL_INFO_LINES_PER_PAGE = 45

export class FacturaePageGenerator implements PageGenerator {
    totalLines=0
    detailLines=0
    hasFooter=false
    hasAdditionalInfo=false
    additionalInfoChunks: string[] = []
    additionalInfoNumPages=0
    invoiceable: Invoiceable
    rectified_inv: Invoiceable|undefined
    pages:FacturaePage[]=[]
    configuration:Configuration
    productConfiguration: ProductConfiguration|undefined

    constructor(invoiceable: Invoiceable, configuration: Configuration, rectified_inv?: Invoiceable, productConfiguration?: ProductConfiguration) {
        this.invoiceable = invoiceable
        this.configuration = configuration
        this.rectified_inv = rectified_inv
        this.productConfiguration = productConfiguration

        const computed_lines = invoiceable.get_lines_with_additional_descriptions()
        this.detailLines = computed_lines.length
        this.totalLines = this.detailLines + 1  // +1 para totales

        this.hasAdditionalInfo = !!(invoiceable.additional_info && invoiceable.additional_info.trim())
        if (this.hasAdditionalInfo) {
            const lines = invoiceable.additional_info.split('\n')
            for (let i = 0; i < lines.length; i += ADDITIONAL_INFO_LINES_PER_PAGE) {
                this.additionalInfoChunks.push(lines.slice(i, i + ADDITIONAL_INFO_LINES_PER_PAGE).join('\n'))
            }
            this.additionalInfoNumPages = this.additionalInfoChunks.length
            this.totalLines += this.additionalInfoNumPages
        }

        this.hasFooter = !!(this.configuration && (this.configuration.footer || this.configuration.footer_small))
        if (this.hasFooter) {
            this.totalLines++
        }
    }
    async getInitialPage(): Promise<Page> {
        const firstPage = new FacturaePage(this.invoiceable, this.configuration, this.rectified_inv, this.productConfiguration, this.additionalInfoChunks)
        firstPage.showHeader = true
        firstPage.showLines = true
        firstPage.showLinesHeader = true
        this.pages.push(firstPage)
        return firstPage
    }
    async getNextPage(page: Page): Promise<Page> {
        const actualPage = page as FacturaePage
        const newPage = new FacturaePage(this.invoiceable, this.configuration, this.rectified_inv, this.productConfiguration, this.additionalInfoChunks)

        newPage.firstPrintedLine = actualPage.lastPrintedLine
        newPage.lastPrintedLine = actualPage.lastPrintedLine

        const hasMoreDetail = newPage.firstPrintedLine < (this.detailLines - 1)
        newPage.showLines = hasMoreDetail
        newPage.showLinesHeader = hasMoreDetail

        this.pages.push(newPage)
        return newPage
    }
    async canGrow(page: Page): Promise<boolean> {
        const facturaePage = page as FacturaePage
        if (facturaePage.pageComplete) return false
        return facturaePage.lastPrintedLine < this.totalLines - 1
    }

    async grow(page: FacturaePage): Promise<void> {
        const actualPage = page as FacturaePage
        actualPage.lastPrintedLine++
        return new Promise((resolve) => {
            nextTick(() => resolve())
        })
    }

    async shrink(page: FacturaePage): Promise<void> {
        const actualPage = page as FacturaePage

        // Si la página solo tiene 1 elemento que no cabe, truncar en vez de mover
        if (actualPage.lastPrintedLine <= actualPage.firstPrintedLine + 1) {
            if (actualPage.firstPrintedLine >= 0 && actualPage.firstPrintedLine === actualPage.lastPrintedLine - 1) {
                actualPage.pageComplete = true
                return Promise.resolve()
            }
        }

        actualPage.lastPrintedLine--
        return new Promise((resolve) => {
            nextTick(() => resolve())
        })
    }
}

export class FacturaeProvider implements ContentProvider {
    invoiceable: Invoiceable
    configuration: Configuration
    rectified_inv: Invoiceable|undefined
    productConfiguration: ProductConfiguration|undefined

    constructor(invoiceable: Invoiceable, configuration: Configuration, rectified_inv?: Invoiceable, productConfiguration?: ProductConfiguration) {
        this.invoiceable = invoiceable
        this.configuration = configuration
        this.rectified_inv = rectified_inv
        this.productConfiguration = productConfiguration
    }
    async getContent() {
        return [new FacturaePageGenerator(this.invoiceable, this.configuration, this.rectified_inv, this.productConfiguration)]
    }
}